import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { GelanggangDetail, NoticeItem, UserProfile, AvailabilityEntry } from '../../types';
import { getGoogleMapsUrl, getGelanggangDisplayAddress, formatPublicName, isManagementOrTrainer } from '../../utils/formatters';
import { parseGelanggangClasses } from '../../utils/pricing';
import { getAvailabilityDB, saveAvailabilityDB, deleteAvailabilityDB } from '../../services/db';
import { useToast } from './Toast';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User,
  Users,
  Target,
  Navigation,
  Bell,
  Info,
  Printer,
  Share2,
  HandFist,
  CalendarCheck2,
  CheckCircle2,
  ArrowLeft
} from 'lucide-react';

interface GelanggangCalendarViewProps {
  gelanggang: GelanggangDetail;
  onBack: () => void;
  notices?: NoticeItem[];
  membersList?: UserProfile[];
  currentUser?: UserProfile;
}

const AVAILABILITY_TIME_SLOTS = [
  '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00',
  '19:00', '20:00', '21:00', '22:00'
];

function formatHour(h: number): string {
  if (h === 0 || h === 24) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

function formatSlotLabel(slot: string): string {
  const h = parseInt(slot.split(':')[0], 10);
  return formatHour(h);
}

function formatSlotRange(slot: string): string {
  const h = parseInt(slot.split(':')[0], 10);
  return `${formatHour(h)} – ${formatHour(h + 1)}`;
}

function formatContiguousSlotRange(startSlot: string, endSlot: string): string {
  const startH = parseInt(startSlot.split(':')[0], 10);
  const endH = parseInt(endSlot.split(':')[0], 10) + 1;
  return `${formatHour(startH)} – ${formatHour(endH)}`;
}

function groupContiguousSlots(slots: string[]): Array<{ start: string; end: string; label: string; durationHours: number }> {
  if (!slots || slots.length === 0) return [];
  const sorted = [...slots].sort();
  const ranges: Array<{ start: string; end: string; label: string; durationHours: number }> = [];

  let currentStart = sorted[0];
  let currentEnd = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const prevH = parseInt(currentEnd.split(':')[0], 10);
    const currH = parseInt(sorted[i].split(':')[0], 10);

    if (currH === prevH + 1) {
      currentEnd = sorted[i];
    } else {
      const sH = parseInt(currentStart.split(':')[0], 10);
      const eH = parseInt(currentEnd.split(':')[0], 10) + 1;
      ranges.push({
        start: currentStart,
        end: currentEnd,
        label: formatContiguousSlotRange(currentStart, currentEnd),
        durationHours: eH - sH,
      });
      currentStart = sorted[i];
      currentEnd = sorted[i];
    }
  }

  const sH = parseInt(currentStart.split(':')[0], 10);
  const eH = parseInt(currentEnd.split(':')[0], 10) + 1;
  ranges.push({
    start: currentStart,
    end: currentEnd,
    label: formatContiguousSlotRange(currentStart, currentEnd),
    durationHours: eH - sH,
  });

  return ranges;
}

const MONTH_NAMES_MS = [
  'Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
  'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'
];

const DAY_NAMES_HEADER = [
  { short: 'Isn', full: 'Isnin' },
  { short: 'Sel', full: 'Selasa' },
  { short: 'Rab', full: 'Rabu' },
  { short: 'Kha', full: 'Khamis' },
  { short: 'Jum', full: 'Jumaat' },
  { short: 'Sab', full: 'Sabtu' },
  { short: 'Ahd', full: 'Ahad' },
];

const MALAY_DAYS = [
  { name: 'ahad', dayIndex: 0 },
  { name: 'isnin', dayIndex: 1 },
  { name: 'selasa', dayIndex: 2 },
  { name: 'rabu', dayIndex: 3 },
  { name: 'khamis', dayIndex: 4 },
  { name: 'jumaat', dayIndex: 5 },
  { name: 'sabtu', dayIndex: 6 },
];

function parseTrainingDays(sessionTime?: string): number[] {
  if (!sessionTime) return [5, 6];
  const lower = sessionTime.toLowerCase();
  const days: number[] = [];

  MALAY_DAYS.forEach(({ name, dayIndex }) => {
    if (lower.includes(name)) {
      days.push(dayIndex);
    }
  });

  return days.length > 0 ? days : [5, 6];
}

function parseTimeOnly(sessionTime?: string): string {
  if (!sessionTime) return '8.00 AM';
  const parenMatch = sessionTime.match(/\(([^)]+)\)/);
  if (parenMatch) {
    const inside = parenMatch[1];
    if (inside.includes(',')) {
      return inside.split(',')[1].trim();
    }
    if (/AM|PM|pagi|malam/i.test(inside)) {
      return inside.trim();
    }
  }
  const timeMatch = sessionTime.match(/\d{1,2}(?:[.:]\d{2})?\s*(?:AM|PM|pagi|malam|petang)/i);
  if (timeMatch) return timeMatch[0];
  return sessionTime;
}

export const GelanggangCalendarView: React.FC<GelanggangCalendarViewProps> = ({
  gelanggang,
  onBack,
  notices = [],
  membersList = [],
  currentUser,
}) => {
  const { showToast } = useToast();
  const today = useMemo(() => new Date(), []);

  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [selectedDateStr, setSelectedDateStr] = useState<string>(() => {
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const trainingDays = useMemo(() => {
    return parseTrainingDays(gelanggang.sessionTime);
  }, [gelanggang.sessionTime]);

  const sessionTimeOnly = useMemo(() => {
    return parseTimeOnly(gelanggang.sessionTime);
  }, [gelanggang.sessionTime]);

  const classes = useMemo(() => {
    return parseGelanggangClasses(gelanggang.name, gelanggang.classes);
  }, [gelanggang]);

  // Active pesilat for this gelanggang
  const enrolledCount = useMemo(() => {
    return membersList.filter(
      (m) => m.gelanggang === gelanggang.name && m.status === 'Aktif'
    ).length;
  }, [gelanggang, membersList]);

  // Generate calendar grid
  const calendarCells = useMemo(() => {
    const cells = [];
    const firstDay = new Date(year, month, 1);
    const startingDayIndex = (firstDay.getDay() + 6) % 7;
    const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    // Previous month padding
    for (let i = startingDayIndex - 1; i >= 0; i--) {
      const prevDate = new Date(year, month - 1, daysInPrevMonth - i);
      const y = prevDate.getFullYear();
      const m = String(prevDate.getMonth() + 1).padStart(2, '0');
      const d = String(prevDate.getDate()).padStart(2, '0');
      cells.push({
        date: prevDate,
        dateString: `${y}-${m}-${d}`,
        dayNumber: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false,
        dayOfWeek: prevDate.getDay(),
      });
    }

    // Current month days
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    for (let day = 1; day <= daysInCurrentMonth; day++) {
      const curDate = new Date(year, month, day);
      const y = curDate.getFullYear();
      const m = String(curDate.getMonth() + 1).padStart(2, '0');
      const d = String(day).padStart(2, '0');
      const dateString = `${y}-${m}-${d}`;
      cells.push({
        date: curDate,
        dateString,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: dateString === todayStr,
        dayOfWeek: curDate.getDay(),
      });
    }

    // Next month padding
    const remaining = (7 - (cells.length % 7)) % 7;
    for (let day = 1; day <= remaining; day++) {
      const nextDate = new Date(year, month + 1, day);
      const y = nextDate.getFullYear();
      const m = String(nextDate.getMonth() + 1).padStart(2, '0');
      const d = String(day).padStart(2, '0');
      cells.push({
        date: nextDate,
        dateString: `${y}-${m}-${d}`,
        dayNumber: day,
        isCurrentMonth: false,
        isToday: false,
        dayOfWeek: nextDate.getDay(),
      });
    }

    return cells;
  }, [year, month, today]);

  // Relevant notices
  const gelanggangNotices = useMemo(() => {
    return notices.filter((n) => {
      if (!n.eventDate) return false;
      return (
        !n.targetGelanggang ||
        n.targetGelanggang === 'Semua Gelanggang' ||
        n.targetGelanggang === 'PSSASMM' ||
        n.targetGelanggang === gelanggang.name
      );
    });
  }, [gelanggang, notices]);

  // Count training sessions this month
  const sessionsThisMonthCount = useMemo(() => {
    return calendarCells.filter(
      (c) => c.isCurrentMonth && trainingDays.includes(c.dayOfWeek)
    ).length;
  }, [calendarCells, trainingDays]);

  // Count events this month
  const eventsThisMonthCount = useMemo(() => {
    const curMonthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;
    return gelanggangNotices.filter((n) => n.eventDate?.startsWith(curMonthPrefix)).length;
  }, [gelanggangNotices, year, month]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleGoToday = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    setSelectedDateStr(`${y}-${m}-${d}`);
  };

  const handleShare = async () => {
    const calendarUrl = `${window.location.origin}/gelanggang?view=calendar&id=${gelanggang.id}`;
    const shareTitle = `Info & Tetapan Gelanggang | PSSASMM Kedah`;
    const shareText = `Jadual Latihan Gelanggang ${gelanggang.name} (PSSASMM):\n🗓️ ${MONTH_NAMES_MS[month]} ${year}\n⏰ Sesi Latihan: ${gelanggang.sessionTime}\n📍 Lokasi: ${gelanggang.location}\n📞 Ketua: ${gelanggang.leaderName}\n🔗 Pautan: ${calendarUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: calendarUrl,
        });
        showToast('success', 'Jadual Dikongsi', 'Pautan jadual gelanggang berjaya dikongsi.');
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      }
    }

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(calendarUrl);
        showToast('success', 'Pautan Jadual Disalin', 'Pautan jadual gelanggang telah disalin ke papan keratan.');
      } catch {
        showToast('error', 'Ralat Menyalin', 'Gagal menyalin pautan.');
      }
    }
  };

  const selectedDateObj = new Date(selectedDateStr + 'T00:00:00');
  const selectedDayOfWeek = selectedDateObj.getDay();
  const isSelectedTrainingDay = trainingDays.includes(selectedDayOfWeek);
  const selectedNotices = gelanggangNotices.filter(
    (n) => n.eventDate?.split('T')[0] === selectedDateStr
  );

  const selectedDayName = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'][
    selectedDayOfWeek
  ];
  const selectedDayNum = selectedDateObj.getDate();
  const selectedMonthName = MONTH_NAMES_MS[selectedDateObj.getMonth()];
  const selectedYear = selectedDateObj.getFullYear();
  const formattedSelectedDate = `${selectedDayName}, ${selectedDayNum} ${selectedMonthName} ${selectedYear}`;

  const mapsUrl = getGoogleMapsUrl(gelanggang);

  // ========== AVAILABILITY STATE ==========
  const isManager = isManagementOrTrainer(currentUser);
  const isWarisNonPesilat = currentUser?.role === 'Waris';
  const canSetAvailability = !!currentUser && !isWarisNonPesilat;

  const [availabilityEntries, setAvailabilityEntries] = useState<AvailabilityEntry[]>([]);

  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;

  const loadAvailability = useCallback(() => {
    getAvailabilityDB(gelanggang.name, monthPrefix).then(setAvailabilityEntries).catch(() => {});
  }, [gelanggang.name, monthPrefix]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  // Current user's availability for selected date
  const myAvailId = currentUser ? `avail-${currentUser.id}-${selectedDateStr}` : '';
  const myAvailEntry = availabilityEntries.find(e => e.id === myAvailId);
  const mySlots = new Set<string>(myAvailEntry?.timeSlots || []);

  // Entries for the selected date (all users)
  const selectedDateEntries = useMemo(() => {
    return availabilityEntries.filter(e => e.date === selectedDateStr);
  }, [availabilityEntries, selectedDateStr]);

  // Matching slots: time slots where 2+ people are available
  const matchingSlots = useMemo(() => {
    const slotCounts: Record<string, { count: number; users: { name: string; role: string }[] }> = {};
    selectedDateEntries.forEach(e => {
      e.timeSlots.forEach(s => {
        if (!slotCounts[s]) slotCounts[s] = { count: 0, users: [] };
        slotCounts[s].count++;
        slotCounts[s].users.push({ name: e.userName, role: '' });
      });
    });
    // Enrich with role from membersList
    Object.values(slotCounts).forEach(sc => {
      sc.users = sc.users.map(u => {
        const member = membersList.find(m => m.name === u.name);
        return { name: u.name, role: member?.role || '' };
      });
    });
    return slotCounts;
  }, [selectedDateEntries, membersList]);

  // Consolidated matching slots: merge consecutive slots with identical participant groups into single slot ranges
  const mergedMatchingSlots = useMemo(() => {
    const matched = AVAILABILITY_TIME_SLOTS.filter(s => (matchingSlots[s]?.count || 0) >= 2);
    if (matched.length === 0) return [];

    const userKey = (users: { name: string; role: string }[]) => {
      return users.map(u => u.name).sort().join(':::');
    };

    const results: Array<{
      startSlot: string;
      endSlot: string;
      label: string;
      durationHours: number;
      count: number;
      users: { name: string; role: string }[];
    }> = [];

    let current: {
      startSlot: string;
      endSlot: string;
      users: { name: string; role: string }[];
      count: number;
      key: string;
    } | null = null;

    for (const slot of matched) {
      const info = matchingSlots[slot];
      const key = userKey(info.users);
      const slotH = parseInt(slot.split(':')[0], 10);

      if (current) {
        const prevEndH = parseInt(current.endSlot.split(':')[0], 10);
        if (slotH === prevEndH + 1 && key === current.key) {
          current.endSlot = slot;
          continue;
        } else {
          const startH = parseInt(current.startSlot.split(':')[0], 10);
          const endH = parseInt(current.endSlot.split(':')[0], 10) + 1;
          results.push({
            startSlot: current.startSlot,
            endSlot: current.endSlot,
            label: formatContiguousSlotRange(current.startSlot, current.endSlot),
            durationHours: endH - startH,
            count: current.count,
            users: current.users,
          });
        }
      }

      current = {
        startSlot: slot,
        endSlot: slot,
        users: info.users,
        count: info.count,
        key,
      };
    }

    if (current) {
      const startH = parseInt(current.startSlot.split(':')[0], 10);
      const endH = parseInt(current.endSlot.split(':')[0], 10) + 1;
      results.push({
        startSlot: current.startSlot,
        endSlot: current.endSlot,
        label: formatContiguousSlotRange(current.startSlot, current.endSlot),
        durationHours: endH - startH,
        count: current.count,
        users: current.users,
      });
    }

    return results;
  }, [matchingSlots]);

  // Availability counts per date (for calendar cell badges)
  const availCountByDate = useMemo(() => {
    const map: Record<string, { totalUsers: number; hasMyEntry: boolean }> = {};
    availabilityEntries.forEach(e => {
      if (!map[e.date]) map[e.date] = { totalUsers: 0, hasMyEntry: false };
      map[e.date].totalUsers++;
      if (currentUser && e.userId === currentUser.id) {
        map[e.date].hasMyEntry = true;
      }
    });
    return map;
  }, [availabilityEntries, currentUser]);

  const handleToggleSlot = async (slot: string) => {
    if (!currentUser || !canSetAvailability) return;
    const newSlots = new Set(mySlots);
    if (newSlots.has(slot)) {
      newSlots.delete(slot);
    } else {
      newSlots.add(slot);
    }
    const slotsArr = Array.from(newSlots).sort();
    const entry: AvailabilityEntry = {
      id: myAvailId,
      userId: currentUser.id,
      userName: currentUser.name,
      gelanggang: gelanggang.name,
      date: selectedDateStr,
      timeSlots: slotsArr,
      updatedAt: new Date().toISOString(),
    };
    // Optimistic update
    setAvailabilityEntries(prev => {
      const without = prev.filter(e => e.id !== myAvailId);
      if (slotsArr.length > 0) {
        return [...without, entry];
      }
      return without;
    });
    if (slotsArr.length > 0) {
      await saveAvailabilityDB(entry);
    } else {
      // Remove entry if no slots selected
      await deleteAvailabilityDB(myAvailId);
    }
  };

  const isSelectedDateFuture = (() => {
    const sel = new Date(selectedDateStr + 'T23:59:59');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return sel >= now;
  })();

  return (
    <div className="space-y-6 animate-fade-in print:space-y-4">
      {/* Top Breadcrumb & Navigation Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-3xl border border-slate-200 shadow-2xs">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-black rounded-xl transition-all shadow-xs cursor-pointer border-none shrink-0 w-fit"
        >
          <ArrowLeft className="w-4 h-4 text-asmm-red" />
          <span>Kembali ke Senarai Gelanggang</span>
        </button>

        <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
          <span className="cursor-pointer hover:text-slate-800" onClick={onBack}>
            Info & Tetapan Gelanggang
          </span>
          <span>/</span>
          <span className="text-asmm-red font-black">Kalendar Sesi ({gelanggang.name})</span>
        </div>
      </div>

      {/* Hero Gelanggang Banner Card */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-5 sm:p-6 text-white shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-asmm-red/15 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider bg-asmm-red text-white px-2.5 py-0.5 rounded-md">
                Gelanggang Rasmi
              </span>
              <span className="text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-md">
                {gelanggang.status}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white flex items-center gap-2.5">
              <span>{gelanggang.name}</span>
            </h2>
            <p className="text-xs text-slate-300 flex items-center gap-1.5 flex-wrap font-medium">
              <MapPin className="w-3.5 h-3.5 text-asmm-red shrink-0" />
              <span>{getGelanggangDisplayAddress(gelanggang)}</span>
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl backdrop-blur-xs border border-white/10 transition-all cursor-pointer"
            >
              <Navigation className="w-4 h-4 text-blue-400" />
              <span>Navigasi GPS</span>
            </a>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl backdrop-blur-xs border border-white/10 transition-all cursor-pointer"
              title="Salin maklumat jadual"
            >
              <Share2 className="w-4 h-4 text-amber-300" />
              <span>Kongsi Jadual</span>
            </button>
          </div>
        </div>

        {/* Quick Info Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-5 mt-5 border-t border-white/10 text-xs">
          <div className="space-y-0.5">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Waktu Latihan</span>
            <span className="text-white font-black text-sm block truncate">{gelanggang.sessionTime}</span>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Ketua Gelanggang</span>
            <span className="text-white font-black text-sm block truncate">{formatPublicName(gelanggang.leaderName)}</span>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Sesi Bulan Ini</span>
            <span className="text-emerald-400 font-black text-sm block">{sessionsThisMonthCount} Sesi Latihan</span>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Pesilat Aktif</span>
            <span className="text-amber-300 font-black text-sm block">{enrolledCount} Pesilat</span>
          </div>
        </div>
      </div>

      {/* Month Navigator and Legend */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-3xl border border-slate-200 shadow-2xs">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="p-2.5 hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-200 shadow-2xs transition-all cursor-pointer hover:border-slate-300"
            title="Bulan Sebelumnya"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="min-w-[190px] text-center">
            <h3 className="text-lg font-black text-slate-900 leading-tight">
              {MONTH_NAMES_MS[month]} {year}
            </h3>
            <span className="text-[10px] font-bold text-slate-500 block">
              {sessionsThisMonthCount} Sesi Latihan • {eventsThisMonthCount} Acara Rasmi
            </span>
          </div>

          <button
            type="button"
            onClick={handleNextMonth}
            className="p-2.5 hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-200 shadow-2xs transition-all cursor-pointer hover:border-slate-300"
            title="Bulan Seterusnya"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={handleGoToday}
            className="ml-2 px-3.5 py-2 text-xs font-extrabold text-asmm-red bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-all cursor-pointer"
          >
            Hari Ini
          </button>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3.5 text-xs font-bold text-slate-600 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-asmm-red inline-block" />
            <span>Sesi Latihan ({sessionTimeOnly})</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" />
            <span>Acara / Notis</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
            <span>Hari Ini</span>
          </span>
          {canSetAvailability && (
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-teal-400 inline-block" />
              <span>Ketersediaan</span>
            </span>
          )}
        </div>
      </div>

      {/* Calendar Grid Container */}
      <div className="border border-slate-200 rounded-3xl overflow-hidden bg-white shadow-sm">
        {/* Day of Week Headers */}
        <div className="grid grid-cols-7 bg-slate-100/90 border-b border-slate-200 text-center text-xs font-black text-slate-700">
          {DAY_NAMES_HEADER.map((day, idx) => {
            const isWeekendDay = idx >= 4;
            return (
              <div
                key={day.short}
                className={`py-3 px-1 text-center border-r last:border-r-0 border-slate-200 ${
                  isWeekendDay ? 'bg-red-50/50 text-asmm-red' : ''
                }`}
              >
                <span className="hidden sm:inline">{day.full}</span>
                <span className="sm:hidden">{day.short}</span>
              </div>
            );
          })}
        </div>

        {/* Calendar Day Cells */}
        <div className="grid grid-cols-7 divide-y divide-slate-200">
          {calendarCells.map((cell, idx) => {
            const isSelected = cell.dateString === selectedDateStr;
            const isTrainingDay = cell.isCurrentMonth && trainingDays.includes(cell.dayOfWeek);
            const cellNotices = gelanggangNotices.filter(
              (n) => n.eventDate?.split('T')[0] === cell.dateString
            );
            const hasNotice = cellNotices.length > 0;

            return (
              <div
                key={idx}
                onClick={() => setSelectedDateStr(cell.dateString)}
                className={`min-h-[90px] sm:min-h-[110px] p-2 sm:p-2.5 border-r last:border-r-0 border-slate-200 relative transition-all cursor-pointer flex flex-col justify-between ${
                  cell.isCurrentMonth
                    ? isTrainingDay
                      ? 'bg-red-50/20 hover:bg-red-50/60'
                      : 'bg-white hover:bg-slate-50/80'
                    : 'bg-slate-50/50 text-slate-300'
                } ${
                  isSelected
                    ? 'ring-2 ring-asmm-red ring-inset bg-red-50/60 z-10 shadow-xs'
                    : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs font-black leading-none rounded-lg w-6 h-6 flex items-center justify-center ${
                      cell.isToday
                        ? 'bg-asmm-red text-white shadow-xs'
                        : isSelected
                        ? 'text-asmm-red font-black'
                        : cell.isCurrentMonth
                        ? 'text-slate-800'
                        : 'text-slate-400 font-medium'
                    }`}
                  >
                    {cell.dayNumber}
                  </span>

                  {cell.isToday && (
                    <span className="hidden sm:inline-block text-[9px] font-black uppercase text-asmm-red bg-red-100/80 px-1.5 py-0.5 rounded-full">
                      Kini
                    </span>
                  )}
                </div>

                <div className="space-y-1 mt-1.5">
                  {isTrainingDay && (
                    <div className="bg-asmm-red text-white text-[9px] sm:text-[10px] font-extrabold px-1.5 py-0.5 rounded-md truncate shadow-3xs flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-300 shrink-0" />
                      <span className="truncate">Sesi Latihan</span>
                    </div>
                  )}

                  {hasNotice && (
                    <div className="bg-blue-600 text-white text-[9px] sm:text-[10px] font-extrabold px-1.5 py-0.5 rounded-md truncate shadow-3xs flex items-center gap-1">
                      <Bell className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{cellNotices[0].title}</span>
                    </div>
                  )}

                  {/* Availability indicators */}
                  {cell.isCurrentMonth && availCountByDate[cell.dateString] && (
                    <div className="flex items-center gap-1">
                      {availCountByDate[cell.dateString].hasMyEntry && (
                        <span className="w-2 h-2 rounded-full bg-teal-400 shrink-0 shadow-xs" title="Anda telah tetapkan ketersediaan" />
                      )}
                      {isManager && availCountByDate[cell.dateString].totalUsers > 0 && (
                        <span className="text-[8px] font-black text-teal-700 bg-teal-100 px-1 py-0 rounded" title={`${availCountByDate[cell.dateString].totalUsers} ahli tersedia`}>
                          {availCountByDate[cell.dateString].totalUsers}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Detail View Panel */}
      <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CalendarCheck2 className="w-5 h-5 text-asmm-red shrink-0" />
            <h3 className="text-base font-black text-slate-900">
              Jadual & Aktiviti: <span className="text-asmm-red">{formattedSelectedDate}</span>
            </h3>
          </div>

          {isSelectedTrainingDay && (
            <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 border border-emerald-300 px-3 py-1 rounded-full">
              Sesi Latihan Rasmi
            </span>
          )}
        </div>

        {/* Training Session Card */}
        {isSelectedTrainingDay ? (
          <div className="bg-white border border-red-200 rounded-2xl p-5 space-y-3.5 shadow-2xs">
            <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-red-100 text-asmm-red rounded-xl shrink-0 flex items-center justify-center">
                    <HandFist className="w-4 h-4" />
                  </span>
                  <h4 className="text-base font-black text-slate-900">
                    Sesi Latihan Silat Mingguan ({gelanggang.name})
                  </h4>
                </div>
                <p className="text-xs text-slate-600 font-medium pl-8">
                  Sesi latihan rasmi seni bela diri Silat Awang Sulung Merah Muda. Pesilat diwajibkan memakai pakaian seragam persatuan yang lengkap.
                </p>
              </div>

              <span className="text-xs font-mono font-black text-asmm-red bg-red-50 border border-red-200 px-3 py-1.5 rounded-xl shrink-0">
                {gelanggang.sessionTime}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100 text-xs">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4 text-asmm-red shrink-0" />
                <span>Masa: <strong className="text-slate-900 font-bold">{sessionTimeOnly}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <User className="w-4 h-4 text-asmm-red shrink-0" />
                <span>Jurulatih: <strong className="text-slate-900 font-bold">{formatPublicName(gelanggang.leaderName)}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="w-4 h-4 text-asmm-red shrink-0" />
                <span className="truncate">{gelanggang.location}</span>
              </div>
            </div>

            {/* Classes Offered During This Session */}
            {classes.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                  Kelas Yang Beroperasi Semasa Sesi Ini:
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  {classes.map((c, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-100 text-slate-800 border border-slate-200 px-3 py-1.5 rounded-xl"
                    >
                      {c.name.toLowerCase().includes('memanah') ? (
                        <Target className="w-3.5 h-3.5 text-amber-600" />
                      ) : (
                        <HandFist className="w-3.5 h-3.5 text-asmm-red" />
                      )}
                      <span>{c.name}</span>
                      {c.schedule && (
                        <span className="text-[10px] text-slate-600 bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono">
                          {c.schedule}
                        </span>
                      )}
                      <span className="text-[11px] text-emerald-700 font-black">
                        (RM{c.price}/bln)
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Notices / Events Cards */}
        {selectedNotices.length > 0 && (
          <div className="space-y-2.5">
            {selectedNotices.map((n) => (
              <div
                key={n.id}
                className="bg-blue-50/70 border border-blue-200 rounded-2xl p-4 sm:p-5 space-y-2 text-xs text-blue-950 shadow-2xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-black text-blue-900">
                    <Bell className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="text-sm">{n.title}</span>
                  </div>
                  <span className="text-[10px] font-extrabold uppercase bg-blue-600 text-white px-2.5 py-0.5 rounded-full">
                    {n.type}
                  </span>
                </div>

                <p className="text-xs text-blue-900/80 leading-relaxed">{n.content}</p>

                <div className="flex items-center gap-4 text-xs font-bold text-blue-800 pt-1 flex-wrap">
                  {n.eventTime && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-blue-600" />
                      <span>{n.eventTime}</span>
                    </span>
                  )}
                  {n.eventLocation && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-blue-600" />
                      <span>{n.eventLocation}</span>
                    </span>
                  )}
                  <span>Pengumuman oleh: {n.authorName} ({n.authorRole})</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State when no session or event */}
        {!isSelectedTrainingDay && selectedNotices.length === 0 && !canSetAvailability && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-2">
            <Info className="w-7 h-7 text-slate-400 mx-auto" />
            <h4 className="text-sm font-bold text-slate-700">
              Tiada Sesi Latihan Atau Acara Rasmi Dijadualkan
            </h4>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Sesi latihan lazim di gelanggang ini dijalankan mengikut jadual rasmi: <strong className="text-slate-800">{gelanggang.sessionTime}</strong>.
            </p>
          </div>
        )}

        {/* ========== KETERSEDIAAN AHLI (Availability) ========== */}
        {canSetAvailability && isSelectedDateFuture && (
          <div className="bg-white border border-teal-200 rounded-2xl p-5 space-y-4 shadow-2xs">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="p-1.5 bg-teal-100 text-teal-700 rounded-xl shrink-0 flex items-center justify-center">
                  <Clock className="w-4 h-4" />
                </span>
                <div>
                  <h4 className="text-sm font-black text-slate-900">Ketersediaan Anda</h4>
                  <p className="text-[10px] text-slate-500 font-medium">Tandakan waktu anda lapang untuk latihan tambahan pada {formattedSelectedDate}.</p>
                </div>
              </div>
              {mySlots.size > 0 && (
                <span className="text-[10px] font-black bg-teal-100 text-teal-800 border border-teal-300 px-2.5 py-1 rounded-full">
                  {mySlots.size} slot dipilih
                </span>
              )}
            </div>

            {/* Time Slot Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
              {AVAILABILITY_TIME_SLOTS.map((slot) => {
                const isSelected = mySlots.has(slot);
                const matchCount = matchingSlots[slot]?.count || 0;
                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => handleToggleSlot(slot)}
                    className={`relative py-2.5 px-1 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                      isSelected
                        ? 'bg-teal-600 text-white border-teal-700 shadow-sm'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-teal-50 hover:border-teal-300'
                    }`}
                    title={formatSlotRange(slot)}
                  >
                    <span className="block text-center">{formatSlotLabel(slot)}</span>
                    {isManager && matchCount >= 2 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 text-[8px] font-black bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-xs">
                        {matchCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ========== MANAGEMENT: Availability Overview ========== */}
        {isManager && selectedDateEntries.length > 0 && (
          <div className="bg-gradient-to-br from-slate-50 to-teal-50/30 border border-teal-200 rounded-2xl p-5 space-y-4 shadow-2xs">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="p-1.5 bg-teal-600 text-white rounded-xl shrink-0 flex items-center justify-center">
                  <Users className="w-4 h-4" />
                </span>
                <div>
                  <h4 className="text-sm font-black text-slate-900">Ketersediaan Ahli — {formattedSelectedDate}</h4>
                  <p className="text-[10px] text-slate-500 font-medium">{selectedDateEntries.length} ahli telah menandakan ketersediaan mereka pada tarikh ini.</p>
                </div>
              </div>
              <span className="text-[10px] font-black bg-slate-200 text-slate-700 px-2.5 py-1 rounded-full uppercase tracking-wider">
                Pengurusan Sahaja
              </span>
            </div>

            {/* Matching Time Slots (2+ people) */}
            {mergedMatchingSlots.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 font-medium">Tiada slot waktu yang sepadan (2+ ahli tersedia serentak) pada tarikh ini.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-teal-700 uppercase tracking-wider block">Slot Sepadan (2+ Ahli Tersedia):</span>
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                    {mergedMatchingSlots.length} slot sepadan
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {mergedMatchingSlots.map((item, idx) => (
                    <div
                      key={`${item.startSlot}-${item.endSlot}-${idx}`}
                      className="bg-white border border-emerald-200 rounded-xl p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="text-xs font-black text-emerald-800">
                            {item.label}
                          </span>
                          {item.durationHours > 1 && (
                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md border border-emerald-200">
                              {item.durationHours} jam
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] font-black bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                          {item.count} ahli
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {item.users.map((u, i) => (
                          <span key={i} className="text-[10px] font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-lg border border-slate-200">
                            {u.name}{u.role ? ` (${u.role})` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Members List */}
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Semua Ketersediaan Ahli:</span>
              <div className="space-y-1.5">
                {selectedDateEntries.map(entry => (
                  <div key={entry.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="text-xs font-black text-slate-900 block">{entry.userName}</span>
                      <span className="text-[10px] text-slate-500 font-medium">
                        {(() => {
                          const member = membersList.find(m => m.id === entry.userId);
                          return member?.role || '';
                        })()}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0 max-w-[65%]">
                      {groupContiguousSlots(entry.timeSlots).map((range, rIdx) => (
                        <span key={rIdx} className="text-[9px] font-bold bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 rounded-md flex items-center gap-1 shadow-3xs">
                          <Clock className="w-2.5 h-2.5 text-teal-600 shrink-0" />
                          <span>{range.label}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Page Bottom Navigation Bar */}
      <div className="flex items-center justify-between p-4 bg-white rounded-3xl border border-slate-200 flex-wrap gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black rounded-xl transition-all shadow-xs cursor-pointer border-none"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Kembali ke Senarai Gelanggang</span>
        </button>

        <span className="text-xs font-medium text-slate-500">
          Sebarang pertanyaan jadual boleh diajukan kepada Ketua Gelanggang ({gelanggang.leaderName}).
        </span>
      </div>
    </div>
  );
};
